import { AlertCircle, Check } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { DisplayNameSuggestion } from "./useAuthForm";

type AuthSuggestionsProps = {
  suggestions: DisplayNameSuggestion[];
  onSelect: (suggestion: DisplayNameSuggestion) => void;
  loading?: boolean;
  errorMessage?: string;
};

export const AuthSuggestions = ({ suggestions, onSelect, loading, errorMessage }: AuthSuggestionsProps) => {
  if (loading) {
    return <p className="text-xs text-muted-foreground">Loading suggestions...</p>;
  }

  if (errorMessage) {
    return <p className="text-sm text-destructive">{errorMessage}</p>;
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-0.5 border rounded-md p-1.5 bg-muted/50">
      <p className="text-xs text-muted-foreground px-2 py-1">Click a suggestion to use it:</p>
      {suggestions.map((suggestion, i) => {
        const containerClass = suggestion.avatarUrl
          ? "flex items-center gap-3 w-full text-left px-3 py-2 rounded hover:bg-accent hover:text-accent-foreground transition-colors"
          : "flex items-center justify-between w-full text-left px-3 py-2 rounded hover:bg-accent hover:text-accent-foreground transition-colors";

        return (
          <button key={`${suggestion.name}-${i}`} type="button" className={containerClass} onClick={() => onSelect(suggestion)}>
            {suggestion.avatarUrl && (
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarImage src={suggestion.avatarUrl} alt={suggestion.name} />
                <AvatarFallback>{suggestion.name[0]}</AvatarFallback>
              </Avatar>
            )}
            <div className="flex items-center justify-between flex-1">
              <span className="font-medium">{suggestion.name}</span>
              {suggestion.available ? (
                <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                  <Check className="h-3 w-3" />
                  Available
                </span>
              ) : (
                <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Taken
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};
